import type {FC, RefObject} from 'react'
import {useState, useEffect, useRef} from 'react'
import tw from 'twin.macro'
import styled from 'styled-components'

const MaskWrapper = styled.div`
  ${tw`fixed left-0 top-0 w-screen h-screen pointer-events-none`}

  opacity: 0.7;
  z-index: 99999;
  color: #000;
`

const Svg = styled.svg<{
  width: number
  height: number
  paddingX: number
  paddingY: number
}>`
  width: ${({width}) => width}px;
  height: ${({height}) => height}px;
  left: ${({paddingX}) => Number(paddingX) / 2}px;
  top: ${({paddingY}) => Number(paddingY) / 2}px;
  position: fixed;
`

const MaskAreaReact = styled.rect<{
  x: number
  y: number
  width: number
  height: number
  rx?: number
}>`
  x: ${({x}) => x}px;
  y: ${({y}) => y}px;
  width: ${({width}) => width}px;
  height: ${({height}) => height}px;
  fill: black;
  rx: ${({rx}) => rx || '0'}px;
`

const MaskRect = styled.rect<{width: number; height: number}>`
  width: ${({width}) => `${width}px`};
  height: ${({height}) => `${height}px`};
  fill: currentColor;
  x: 0;
  y: 0;
  mask: url(#mask);
`

const ClickAreaRect = styled.rect<{width: number; height: number}>`
  width: ${({width}) => `${width}px`};
  height: ${({height}) => `${height}px`};
  x: 0;
  y: 0;
  fill: currentColor;
  pointer-events: auto;
  clip-path: url(#clip);
`

const HighlightedAreaRect = styled.rect<{
  x: number
  y: number
  width: number
  height: number
}>`
  x: ${({x}) => `${x}px`};
  y: ${({y}) => `${y}px`};
  width: ${({width}) => `${width}px`};
  height: ${({height}) => `${height}px`};
  pointer-events: auto;
  fill: transparent;
  display: none;
`

const maskID = 'mask'
const clipID = 'clip'

const maskAreaStyles = {
  x: 100,
  y: 100,
  width: 100,
  height: 100,
  fill: 'black',
  rx: 0,
}

function safe(sum: number): number {
  return sum < 0 ? 0 : sum
}

export type HighlightedRect = {
  top: number
  left: number
  width: number
  height: number
}

// Rewrite based on elrumordelaluz/reactour
// @see https://github.com/elrumordelaluz/reactour/blob/master/packages/mask/src/Mask.tsx
export const ClipMask: FC<{
  rect: HighlightedRect
  onClick?: () => unknown
  onClickHighlighted?: () => unknown
}> = ({rect, onClick, onClickHighlighted}) => {
  const [px, py] = [10, 10]
  const [wpx, wpy] = [0, 0]
  const w = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0
  )
  const h = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight || 0
  )
  const width = safe(rect.width + px * 2)
  const height = safe(rect.height + py * 2)
  const top = safe(rect.top - py - wpy / 2)
  const left = safe(rect.left - px - wpx / 2)
  const windowWidth = w - wpx
  const windowHeight = h - wpy

  return (
    <MaskWrapper onClick={onClick}>
      <Svg
        width={windowWidth}
        height={windowHeight}
        paddingX={wpx}
        paddingY={wpy}
      >
        <defs>
          <mask id={maskID}>
            <rect
              x={0}
              y={0}
              width={windowWidth}
              height={windowHeight}
              fill="white"
            />
            <MaskAreaReact
              x={left}
              y={top}
              width={width}
              height={height}
              // Needs for Safari, as we pass any value, css rx will apply.
              rx={maskAreaStyles.rx ? 1 : undefined}
            />
          </mask>
          <clipPath id={clipID}>
            <polygon
              points={`0 0, 0 ${windowHeight}, ${left} ${windowHeight}, ${left} ${top}, ${
                left + width
              } ${top}, ${left + width} ${top + height}, ${left} ${
                top + height
              }, ${left} ${windowHeight}, ${windowWidth} ${windowHeight}, ${windowWidth} 0`}
            />
          </clipPath>
        </defs>
        <MaskRect id="mask" width={windowWidth} height={windowHeight} />
        <ClickAreaRect
          id="clickArea"
          width={windowWidth}
          height={windowHeight}
        />
        <HighlightedAreaRect
          id="highlightedArea"
          x={left}
          y={top}
          width={width}
          height={height}
          onClick={onClickHighlighted}
        />
      </Svg>
    </MaskWrapper>
  )
}

export function useRectRef<T = HTMLElement>() {
  const ref = useRef<T>()
  const [rect, setRect] = useState<DOMRect | null>(null)
  useEffect(() => {
    if (ref.current) {
      setRect((ref.current as unknown as HTMLElement).getBoundingClientRect())
    }
  }, [ref])
  return [ref, rect] as [RefObject<T>, DOMRect | null]
}
